import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { settingsService, SiteSetting } from "../services/settings";
import { featureFlagsService } from "../services/featureFlags";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Switch } from "../components/ui/Switch";
import { toast } from "sonner";
import { Smartphone, Type, Save, QrCode } from "lucide-react";

export function ConfiguracoesLojaPage() {
  const { user } = useOutletContext<{ user?: { email?: string } }>() || {};
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isPixEnabled, setIsPixEnabled] = useState(false);
  const [loadingPix, setLoadingPix] = useState(false);
  const [updatingPix, setUpdatingPix] = useState(false);

  const isRonald = user?.email === 'ronald@ronald.com';

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (isRonald) {
      fetchPixFlag();
    }
  }, [isRonald]);

  const fetchPixFlag = async () => {
    setLoadingPix(true);
    try {
      const response = await featureFlagsService.getFlag('pix_payment');
      const data: any = response.data;
      const enabled = data?.enabled ?? data?.data?.enabled ?? false;
      setIsPixEnabled(enabled);
    } catch (error) {
      console.error("Erro ao carregar status do PIX:", error);
      toast.error("Erro ao carregar status do PIX");
    } finally {
      setLoadingPix(false);
    }
  };

  const handlePixToggle = async (checked: boolean) => {
    setUpdatingPix(true);
    try {
      await featureFlagsService.updateFlag('pix_payment', checked);
      setIsPixEnabled(checked);
      toast.success(`PIX ${checked ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error) {
      console.error("Erro ao atualizar status do PIX:", error);
      toast.error("Erro ao atualizar status do PIX");
    } finally {
      setUpdatingPix(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getAll();
      const data = response.data?.data || [];
      setSettings(data);

      const initialData: Record<string, string> = {};
      data.forEach((s: SiteSetting) => {
        initialData[s.key] = s.value;
      });
      setFormData(initialData);
    } catch (error) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsService.updateMultiple(formData);
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const getSettingsByGroup = (group: string) => {
    return settings.filter((s) => s.group === group);
  };

  if (loading) {
    return (
      <main className="flex-1 p-6 bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </main>
    );
  }

  const inputClass = "bg-white border-gray-300 focus:border-[#D4C070] focus:ring-[#D4C070]";

  return (
    <main className="flex-1 p-6 bg-gray-50 pb-32">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Configurações da Loja</h1>
          <p className="text-gray-600">Gerencie contatos, textos e comportamentos do site público.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Grupo: Feature Flags (Visível apenas para ronald@ronald.com) */}
          {isRonald && (
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
              <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
                <QrCode className="h-5 w-5 text-[#D4C070]" /> Método de Pagamento PIX
              </h2>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="space-y-1">
                  <h4 className="font-bold text-gray-900">Pagamento via PIX</h4>
                  <p className="text-sm text-gray-500">Ativar ou desativar o método de pagamento PIX na loja.</p>
                </div>
                {loadingPix ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                ) : (
                  <Switch
                    checked={isPixEnabled}
                    disabled={updatingPix}
                    onCheckedChange={handlePixToggle}
                  />
                )}
              </div>
            </div>
          )}

          {/* Grupo: Contato */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#D4C070]" /> Informações de Contato
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {getSettingsByGroup('contato').map((setting) => (
                <div key={setting.id} className="space-y-2">
                  <Label>{setting.label}</Label>
                  <Input
                    className={inputClass}
                    value={formData[setting.key] || ''}
                    onChange={(e) => handleChange(setting.key, e.target.value)}
                    placeholder={setting.type === 'phone' ? 'Ex: 5511999999999' : ''}
                  />
                  {setting.type === 'phone' && (
                    <p className="text-[10px] text-gray-400">Insira DDI + DDD + Número. Ex: 5511999999999</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Grupo: Loja (Textos e Banners) */}
          <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <h2 className="text-xl font-bold text-gray-700 flex items-center gap-2">
              <Type className="h-5 w-5 text-[#D4C070]" /> Textos e Visuais da Loja
            </h2>
            <div className="space-y-6">
              {getSettingsByGroup('loja').map((setting) => (
                <div key={setting.id} className="space-y-2">
                  <Label>{setting.label}</Label>
                  {setting.type === 'textarea' ? (
                    <textarea
                      className={`w-full p-3 rounded-md border resize-y min-h-[100px] outline-none ${inputClass}`}
                      value={formData[setting.key] || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                    />
                  ) : (
                    <Input
                      className={inputClass}
                      value={formData[setting.key] || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                    />
                  )}
                  {setting.key === 'topbanner_text' && (
                    <div className="mt-2 p-3 bg-gray-900 overflow-hidden rounded-md flex items-center h-10">
                      <div className="animate-marquee whitespace-nowrap text-white text-xs font-medium tracking-widest uppercase">
                        {formData[setting.key] || 'TEXTO DE EXEMPLO PARA O BANNER SUPERIOR'}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Floating Action Bar */}
          <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white/90 backdrop-blur-md border-t p-4 flex items-center justify-between z-50 shadow-2xl">
            <span className="hidden md:block text-xs text-gray-500">
              Modificações não salvas serão perdidas.
            </span>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#D4C070] text-white px-10 shadow-lg shadow-[#D4C070]/20 font-bold"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvando..." : "Salvar Configurações"}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}

